import { useEffect, useMemo, useState } from "react";
import { Building2, User, Disc3, ChevronDown, ChevronUp, Search } from "lucide-react";
import { adminLabelOverview } from "@/lib/afmApi";
import { Loading, SectionTitle } from "@/components/common";
import AssignToManager from "@/components/AssignToManager";
import AssignToArtist from "@/components/AssignToArtist";
import ReleaseReports from "@/components/ReleaseReports";
import { BucketTabs, StatusCell, bucketOf, countBuckets } from "@/components/ReleaseStatus";

const KINDS = [
  { key: "all", label: "Todo" },
  { key: "subcuenta", label: "Subcuentas" },
  { key: "cuenta", label: "Cuentas individuales" },
  { key: "sello", label: "Sello sin asignar" },
];
const sel = "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black";
const KIND_LABEL = { subcuenta: "Subcuenta", cuenta: "Cuenta", sello: "Always Fresh" };

function GroupTable({ title, rows, onPick }) {
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="px-5 py-3 font-semibold">{title}</th>
            <th className="px-3 py-3 text-right font-semibold">Lanzamientos</th>
            <th className="px-3 py-3 text-right font-semibold">Enviados</th>
            <th className="px-3 py-3 text-right font-semibold">En revisión</th>
            <th className="px-3 py-3 text-right font-semibold">Por corregir</th>
            <th className="px-5 py-3 text-right font-semibold">Rechazados</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((g) => (
            <tr key={g.key} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" onClick={() => onPick(g)}>
              <td className="px-5 py-3"><div className="font-medium text-black">{g.name}</div>{g.sub && <div className="text-xs text-slate-500">{g.sub}</div>}</td>
              <td className="px-3 py-3 text-right font-mono tabular-nums">{g.c.all}</td>
              <td className="px-3 py-3 text-right font-mono tabular-nums text-emerald-700">{g.c.tiendas}</td>
              <td className="px-3 py-3 text-right font-mono tabular-nums text-slate-600">{g.c.revision}</td>
              <td className="px-3 py-3 text-right font-mono tabular-nums text-red-700">{g.c.corregir}</td>
              <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-500">{g.c.rechazado}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Panorama de la disquera: todo el catálogo, filtrando por cuenta, subcuenta o artista.
export default function AdminPanorama() {
  const [d, setD] = useState(undefined);
  const [kind, setKind] = useState("all");
  const [mid, setMid] = useState("");
  const [artist, setArtist] = useState("");   // id (en subcuenta) o nombre (resto)
  const [account, setAccount] = useState("");
  const [bucket, setBucket] = useState("all");
  const [open, setOpen] = useState(null);

  const load = () => adminLabelOverview().then(setD).catch((e) => setD({ error: e?.response?.data?.detail || "No se pudo cargar" }));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = d?.releases || [];
  const managers = d?.managers || [];
  const mgr = managers.find((m) => m.id === mid);

  const filtered = useMemo(() => rows.filter((r) => {
    if (kind !== "all" && r.kind !== kind) return false;
    if (mid && r.manager_id !== mid) return false;
    if (artist) {
      if (mid ? !(r.artist_ids || []).includes(artist) : !(r.artists || []).some((n) => (n || "").toLowerCase() === artist.toLowerCase())) return false;
    }
    if (account && !(r.account || "").toLowerCase().includes(account.trim().toLowerCase())) return false;
    return true;
  }), [rows, kind, mid, artist, account]);
  const shown = bucket === "all" ? filtered : filtered.filter((r) => bucketOf(r) === bucket);

  const artistOptions = useMemo(() => {
    if (mgr) return mgr.artists.map((a) => ({ value: a.id, label: a.name }));
    const names = new Map();
    filtered.forEach((r) => (r.artists || []).forEach((n) => { if (n) names.set(n.toLowerCase(), n); }));
    return [...names.values()].sort((a, b) => a.localeCompare(b)).map((n) => ({ value: n, label: n }));
  }, [mgr, filtered]);

  // Tabla resumen según el nivel: subcuentas → artistas de una subcuenta → cuentas individuales
  const groups = useMemo(() => {
    if (artist) return { title: "", rows: [] };
    if (mid && mgr) {
      return { title: `Artistas de ${mgr.name}`, level: "artist", rows: mgr.artists.map((a) => {
        const rs = filtered.filter((r) => (r.artist_ids || []).includes(a.id));
        return { key: a.id, name: a.name, c: countBuckets(rs) };
      }) };
    }
    if (kind === "subcuenta" || kind === "all") {
      const subs = managers.map((m) => {
        const rs = filtered.filter((r) => r.manager_id === m.id);
        return { key: m.id, name: m.name, sub: `${m.artists.length} artista(s)${m.active ? "" : " · desactivada"}`, c: countBuckets(rs) };
      });
      return { title: "Subcuentas", level: "manager", rows: subs };
    }
    if (kind === "cuenta") {
      const by = new Map();
      filtered.forEach((r) => { const k = r.account || "—"; by.set(k, [...(by.get(k) || []), r]); });
      return { title: "Cuentas", level: "account", rows: [...by.entries()].map(([k, rs]) => ({ key: k, name: k, sub: [...new Set(rs.flatMap((r) => r.artists || []))].join(", "), c: countBuckets(rs) })) };
    }
    return { title: "", rows: [] };
  }, [artist, mid, mgr, kind, managers, filtered]);

  const pickGroup = (g) => {
    if (groups.level === "manager") { setKind("subcuenta"); setMid(g.key); setArtist(""); }
    else if (groups.level === "artist") setArtist(g.key);
    else if (groups.level === "account") setAccount(g.key);
  };

  if (d === undefined) return <Loading />;
  if (d.error) return <p className="text-sm text-slate-500">{d.error}</p>;

  return (
    <div className="space-y-6" data-testid="admin-panorama">
      <div>
        <h1 className="font-serif text-3xl font-extrabold tracking-tight text-black">Panorama del catálogo</h1>
        <p className="mt-1 text-sm text-slate-500">Todo lo que entra a Always Fresh: por cuenta, por subcuenta o por artista. Desde cada lanzamiento puedes asignarlo a cualquier artista de una subcuenta.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {KINDS.map((k) => (
          <button key={k.key} onClick={() => { setKind(k.key); setMid(""); setArtist(""); setAccount(""); }} className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${kind === k.key ? "border-black bg-black text-white" : "border-slate-300 bg-white text-slate-700 hover:border-black"}`}>{k.label}</button>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <select value={mid} onChange={(e) => { setMid(e.target.value); setArtist(""); if (e.target.value) setKind("subcuenta"); }} className={sel} disabled={kind === "cuenta" || kind === "sello"}>
          <option value="">Todas las subcuentas</option>
          {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select value={artist} onChange={(e) => setArtist(e.target.value)} className={sel}>
          <option value="">Todos los artistas</option>
          {artistOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={account} onChange={(e) => setAccount(e.target.value)} placeholder="Cuenta (correo)" className={`${sel} w-full pl-9`} />
        </div>
      </div>

      <BucketTabs rows={filtered} value={bucket} onChange={setBucket} />

      {groups.rows.length > 0 && (
        <section>
          <SectionTitle>{groups.title}</SectionTitle>
          <GroupTable title={groups.level === "account" ? "Cuenta" : groups.level === "artist" ? "Artista" : "Subcuenta"} rows={groups.rows} onPick={pickGroup} />
        </section>
      )}

      <section>
        <SectionTitle>Lanzamientos ({shown.length})</SectionTitle>
        <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
          {shown.slice(0, 300).map((r) => (
            <div key={r.release_id} className="px-5 py-3 text-sm">
              <div className="flex flex-wrap items-center gap-3">
                <Disc3 className="h-4 w-4 text-slate-300" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-black">{r.title || "Sin título"}</div>
                  <div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                    <span>{(r.artists || []).join(" · ") || "—"}</span>
                    {r.release_date && <span>· {r.release_date}</span>}
                    <span className="inline-flex items-center gap-1">· {r.kind === "subcuenta" ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />} {r.manager_name || KIND_LABEL[r.kind]}</span>
                    {r.account && <span>· {r.account}</span>}
                  </div>
                </div>
                <StatusCell r={r} />
                <button onClick={() => setOpen(open === r.release_id ? null : r.release_id)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-black">
                  {r.kind === "subcuenta" ? "Asignación" : "Asignar"} {open === r.release_id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
              </div>
              {open === r.release_id && (
                <div className="mt-3 space-y-3">
                  {r.kind === "sello" && <AssignToArtist releaseId={r.release_id} onDone={load} />}
                  <AssignToManager release={{ id: r.release_id, title: r.title, release_date: r.release_date }} />
                  {r.distribution_state === "forwarded" && <ReleaseReports releaseId={r.release_id} isAdmin />}
                  <button onClick={load} className="text-xs font-semibold text-slate-500 hover:text-black">Actualizar panorama</button>
                </div>
              )}
            </div>
          ))}
          {!shown.length && <p className="px-5 py-8 text-center text-sm text-slate-400">Nada con estos filtros.</p>}
          {shown.length > 300 && <p className="px-5 py-3 text-center text-xs text-slate-400">Mostrando 300 de {shown.length}. Usa los filtros para acotar.</p>}
        </div>
      </section>
    </div>
  );
}
